import { and, employee, qantProductAdmin, qantProductPii } from '../condition.js';
import type { ProductMenuDef } from '../type.js';

/**
 * Neuro (AIオペレーター) のメニュー定義。
 * BASIC グループの path は agentId なしの静的 URL。着地側 Neuro が URL `id` 省略時に
 * localStorage / 最新エージェントで補完してリダイレクトする。
 */
export const QANT_NEURO_MENU = {
  product: {
    name: 'neuro',
    icon: 'qantAiOperator',
    label: { ja: 'AIオペレーター', en: 'AI Operator' },
    description: { ja: 'AIエージェント作成・運用', en: 'AI Operator' },
  },
  menuGroups: {
    BASIC: {
      name: 'basic',
      menus: {
        REPORT: {
          name: 'report',
          icon: 'neuroReport',
          label: { ja: 'レポート', en: 'Report' },
          path: { host: 'qant', path: '/neuro/agents/detail/report' },
        },
        METRICS: {
          name: 'metrics',
          icon: 'neuroMetrics',
          label: { ja: '応対評価', en: 'Quality Review' },
          path: { host: 'qant', path: '/neuro/agents/detail/metrics' },
        },
        ISSUES: {
          name: 'issues',
          icon: 'neuroIssues',
          label: { ja: '課題', en: 'Issues' },
          path: { host: 'qant', path: '/neuro/agents/detail/issues' },
          // 全面 PII のため PII アクセス権限で出し分ける。
          condition: qantProductPii('neuro'),
        },
        CONVERSATIONS: {
          name: 'conversations',
          icon: 'neuroConversations',
          label: { ja: '応対ログ', en: 'Conversations' },
          path: { host: 'qant', path: '/neuro/agents/detail/conversations' },
          // 全面 PII のため PII アクセス権限で出し分ける。
          condition: qantProductPii('neuro'),
        },
        AGENT_SETTINGS: {
          name: 'agentSettings',
          icon: 'neuroAgentSettings',
          label: { ja: 'エージェント構築', en: 'Agent Builder' },
          path: { host: 'qant', path: '/neuro/agents/detail/basic' },
        },
        AI_ASSISTANT: {
          name: 'aiAssistant',
          icon: 'neuroAiAssistant',
          label: { ja: 'AIアシスタント', en: 'AI Assistant' },
          path: { host: 'qant', path: '/neuro/agents/detail/assistant' },
          // PII アクセスと編集権限がある場合に表示する。
          condition: and(qantProductAdmin('neuro'), qantProductPii('neuro')),
        },
      },
    },
    OTHER: {
      name: 'other',
      menus: {
        OTHER: {
          name: 'other',
          icon: 'other',
          label: { ja: 'その他', en: 'Others' },
          popoverMenus: {
            BASIC: {
              name: 'basic',
              items: {
                SPEECH_RECOGNITION_TEST: {
                  name: 'speechRecognitionTest',
                  label: { ja: '音声認識精度テスト', en: 'Speech Recognition Tests' },
                  path: { host: 'qant', path: '/neuro/agents/detail/speech-recognition-tests' },
                  // 音声・正解値・認識結果を含む全面 PII 機能のため、PII アクセス権限で出し分ける。
                  condition: qantProductPii('neuro'),
                },
                ALERT_SETTINGS: {
                  name: 'alertSettings',
                  label: { ja: 'アラート設定', en: 'Alert Settings' },
                  path: { host: 'qant', path: '/neuro/alert-settings' },
                  condition: and(qantProductAdmin('neuro'), qantProductPii('neuro')),
                },
                APPROVAL_SETTINGS: {
                  name: 'approvalSettings',
                  label: { ja: '承認設定', en: 'Approval Settings' },
                  path: { host: 'qant', path: '/neuro/approval-settings' },
                },
              },
            },
          },
        },
      },
    },
    AGENT_SETTINGS: {
      name: 'agent_common',
      label: { ja: 'エージェント共通', en: 'Agent Common' },
      menus: {
        ASSISTANT_SETTINGS: {
          name: 'assistantSettings',
          icon: 'settings',
          label: { ja: 'アシスタント設定', en: 'Assistant Settings' },
          path: { host: 'qant', path: '/neuro/assistant-settings' },
          condition: qantProductAdmin('neuro'),
        },
        NOTIFICATION: {
          name: 'notification',
          icon: 'notification',
          label: { ja: '通知', en: 'Notification' },
          path: { host: 'qant', path: '/neuro/notifications' },
        },
        SETTINGS: {
          name: 'settings',
          icon: 'settings',
          label: { ja: '設定', en: 'Settings' },
          path: { host: 'qant', path: '/neuro/settings' },
          // 社内アカウントだけに表示し、admin-ui のストライプで内部機能と示す。
          options: { adminUi: true },
          condition: employee(),
        },
      },
    },
  },
} as const satisfies ProductMenuDef;
